/** Shared WebRTC signaling utilities for copy/paste (non-trickle) ICE exchange. */
package com.oasisfeng.invaluable.p2p.webrtc

import kotlinx.coroutines.await
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlin.coroutines.resume
import kotlin.js.JSON
import kotlin.js.Promise

object WebRtcSignaling {

    /**
     * Wait until ICE gathering completes so the session description includes candidates.
     *
     * When using copy/paste signaling, supporting trickle ICE would require exchanging multiple blobs
     * (or building a small signaling channel). A short timeout keeps the demo responsive even when
     * the browser never transitions to "complete" (which can happen on some networks).
     */
    suspend fun awaitIceGatheringComplete(pc: dynamic, timeoutMs: Int = 10_000): String {
        if (pc.iceGatheringState == "complete") return "complete"
        suspendCancellableCoroutine { cont ->
            val iceHandler: (dynamic) -> Unit = {
                if (pc.iceGatheringState == "complete" && cont.isActive) {
                    pc.onicegatheringstatechange = null
                    cont.resume(Unit)
                }
            }
            pc.onicegatheringstatechange = iceHandler
            val timeoutHandler: () -> Unit = {
                if (cont.isActive) {
                    pc.onicegatheringstatechange = null
                    cont.resume(Unit)
                }
            }
            val timerId = js("setTimeout(timeoutHandler, timeoutMs)")
            cont.invokeOnCancellation {
                pc.onicegatheringstatechange = null
                js("clearTimeout(timerId)")
            }
        }
        return pc.iceGatheringState as? String ?: "unknown"
    }

    /**
     * Normalize mDNS host candidates for local same-machine testing.
     *
     * Some browser/runtime combinations expose only `.local` hostnames in ICE candidates.
     * For manual copy/paste between tabs on the same machine, resolving those names may fail.
     * We keep the original candidate and add a loopback fallback candidate.
     */
    fun normalizeCandidateLinesForLocalLoopback(candidateLine: String): List<String> {
        val tokens = candidateLine.split(' ')
        if (tokens.size < 6) return listOf(candidateLine)
        val address = tokens[4]
        if (!address.endsWith(".local")) return listOf(candidateLine)

        val normalized = tokens.toMutableList()
        // Keep a distinct foundation so both original (.local) and loopback fallback candidates can coexist.
        // Some browser stacks deduplicate ICE candidates by foundation and would otherwise drop the fallback.
        normalized[0] = tweakCandidateFoundation(normalized[0], suffix = "lb")
        normalized[4] = "127.0.0.1"
        return listOf(candidateLine, normalized.joinToString(" "))
    }

    fun tweakCandidateFoundation(token: String, suffix: String): String {
        if (!token.startsWith("candidate:")) return token
        val foundation = token.removePrefix("candidate:")
        return "candidate:${foundation}${suffix}"
    }

    suspend fun addRemoteCandidates(pc: dynamic, signalingBlob: dynamic): Int {
        val candidates = signalingBlob?.candidates ?: return 0
        val length = candidates.length as? Int ?: return 0
        var added = 0
        for (index in 0 until length) {
            val candidate = candidates[index] ?: continue
            val candidateLine = candidate.candidate as? String ?: continue
            if (candidateLine.isBlank()) continue
            val candidateLines = normalizeCandidateLinesForLocalLoopback(candidateLine)
            var accepted = false
            for (line in candidateLines) {
                val init = js("{}")
                init.candidate = line
                init.sdpMid = candidate.sdpMid
                init.sdpMLineIndex = candidate.sdpMLineIndex
                val success = runCatching { (pc.addIceCandidate(init) as Promise<dynamic>).await() }.isSuccess
                if (success) accepted = true
            }
            if (accepted) added++
        }
        return added
    }

    /** Encode SDP + candidates array into a JSON signaling blob. */
    fun encodeSignalingBlob(pc: dynamic, candidates: List<dynamic>): String {
        val desc = pc.localDescription ?: return ""
        val blob = js("{}")
        blob.type = desc.type
        blob.sdp = desc.sdp
        blob.candidates = candidates.toTypedArray()
        return JSON.stringify(blob)
    }

    fun countCandidates(sdp: String?): Int {
        if (sdp.isNullOrBlank()) return 0
        return sdp.lineSequence().count { it.startsWith("a=candidate:") }
    }

    /** Install debug-logging handlers for peer connection state changes. */
    fun installPeerConnectionDebugHooks(
        pc: dynamic, role: String,
        log: (String) -> Unit,
        onStateChanged: () -> Unit,
    ) {
        pc.onconnectionstatechange = {
            val state = pc.connectionState as? String ?: "unknown"
            log("RTC($role) connectionState=$state")
            onStateChanged()
        }
        pc.oniceconnectionstatechange = {
            log("RTC($role) iceConnectionState=${pc.iceConnectionState}")
            onStateChanged()
        }
        pc.onsignalingstatechange = {
            log("RTC($role) signalingState=${pc.signalingState}")
            onStateChanged()
        }
    }
}
